import { useTranslation } from 'react-i18next';
import Button from '../../components/common/Button';
import type { ModalProps } from '../../components/common/Modal';
import Modal from '../../components/common/Modal';

export type ConfirmModalProps = Pick<ModalProps, 'open' | 'title' | 'onAfterLeave'> & {
    /** Content text to display in the confirmation modal */
    content?: string;
    /** Callback when user confirms the action */
    onConfirm: () => void;
    /** Callback when user rejects/cancels the action */
    onReject: () => void;
    /** Custom text for the OK/Confirm button */
    okText?: string;
    /** Whether this is a deletion action (affects button color and default text) */
    isDeletion?: boolean;
    /** Custom text for the Cancel button */
    cancelText?: string;
};

const ConfirmModal = ({
    content,
    onConfirm,
    okText,
    cancelText,
    onReject,
    isDeletion,
    title,
    ...props
}: ConfirmModalProps) => {
    const { t } = useTranslation('common');

    const customFooter = (
        <div className="mt-5 flex items-center justify-end gap-2.5">
            <Button outline onClick={onReject} compact>
                {cancelText || t('common:confirmModal.actions.cancel')}
            </Button>
            <Button color={isDeletion ? 'danger' : 'teal'} onClick={onConfirm} compact>
                {okText ||
                    (isDeletion ? t('common:confirmModal.actions.delete') : t('common:confirmModal.actions.confirm'))}
            </Button>
        </div>
    );

    return (
        <Modal
            {...props}
            title={title}
            largeTitle
            className="!w-[512px] !max-w-[512px]"
            dataCy="confirmation"
            footer={customFooter}
            onClose={onReject}
            noAction
            showCloseButton={false}
        >
            <div>
                <div className="text-base leading-normal font-medium text-neutral-500">{content}</div>
            </div>
        </Modal>
    );
};

export default ConfirmModal;
